import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Table,
    Input,
    Typography,
    Card,
    Space,
    Spin,
    App,
    Button,
    Tag
} from 'antd';
import {
    SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {authService, paymentMethodService} from '../../services/api';
import { usePageTitle } from '../../context/PageTitleContext';
import debounce from 'lodash/debounce';

const { Text } = Typography;

// Функция для форматирования даты
const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
};

// Функция для отображения типа способа оплаты
const formatPaymentType = (type) => {
    const typeMap = {
        'cash': { label: 'Наличные', color: 'green' },
        'card': { label: 'Карта', color: 'blue' },
        'electronic': { label: 'Электронные', color: 'purple' },
        'other': { label: 'Прочие', color: 'default' }
    };

    const typeInfo = typeMap[type] || { label: type, color: 'default' };
    return <Tag color={typeInfo.color}>{typeInfo.label}</Tag>;
};

const PaymentMethodsList = () => {
    const { setPageTitle, setRightContent } = usePageTitle();
    const navigate = useNavigate();
    const { message } = App.useApp();

    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [nextCursor, setNextCursor] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState(false);

    const observer = useRef();
    const lastPaymentMethodElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                loadMorePaymentMethods();
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore, error]);

    // Устанавливаем заголовок страницы
    useEffect(() => {
        setPageTitle('Способы оплаты');

        setRightContent(
            authService.hasPermission('pos:payment_methods:create') && (<Button
                type="primary"
                onClick={() => navigate('/payment-methods/create')}
            >
                Создать способ оплаты
            </Button>)
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent]);

    // Загрузка записей при первой загрузке и при изменении поиска
    useEffect(() => {
        setError(false);
        fetchPaymentMethods();
    }, [searchText]);

    // Функция для дебаунсинга поиска
    const debouncedSearch = useCallback(
        debounce((value) => {
            setSearchText(value);
        }, 500),
        []
    );

    // Обработчик изменения поля поиска
    const handleSearch = (e) => {
        debouncedSearch(e.target.value);
    };

    // Загрузка записей
    const fetchPaymentMethods = async () => {
        try {
            setLoading(true);
            setPaymentMethods([]);
            setNextCursor(null);
            setHasMore(true);

            const response = await paymentMethodService.getPaymentMethods({
                search: searchText
            });

            if (response && response.status === 'success') {
                setPaymentMethods(response.data.items);
                setNextCursor(response.data.pagination.next_cursor);
                setHasMore(response.data.pagination.has_more);
                setTotal(response.data.pagination.total);
            } else {
                message.error(response?.message || 'Не удалось загрузить список способов оплаты');
            }
        } catch (error) {
            setError(true);
            message.error(error.message || 'Ошибка при загрузке способов оплаты');
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    // Загрузка дополнительных способов оплаты при прокрутке
    const loadMorePaymentMethods = async () => {
        if (!hasMore || loading || error) return;

        try {
            setLoading(true);

            const response = await paymentMethodService.getPaymentMethods({
                cursor: nextCursor,
                search: searchText
            });

            if (response && response.status === 'success') {
                setPaymentMethods(prev => [...prev, ...response.data.items]);
                setNextCursor(response.data.pagination.next_cursor);
                setHasMore(response.data.pagination.has_more);
                setTotal(response.data.pagination.total);
            } else {
                message.error(response?.message || 'Не удалось загрузить больше способов оплаты');
            }
        } catch (error) {
            setError(true);
            message.error(error.message || 'Ошибка при загрузке дополнительных способов оплаты');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            width: 350,
            render: (text, record) => (
                <Text strong>
                    {record.name}
                </Text>
            ),
        },
        {
            title: 'Тип',
            dataIndex: 'type',
            key: 'type',
            width: 120,
            render: (text) => formatPaymentType(text),
        },
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 100,
        },
        {
            title: 'Дата создания',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            render: (text) => formatDate(text),
        },
    ];

    // Создаем данные для таблицы
    const tableData = paymentMethods.map((paymentMethod, index) => ({
        ...paymentMethod,
        key: paymentMethod.id
    }));

    return (
        <>
            <Card style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Space>
                        <Input
                            placeholder="Поиск способов оплаты..."
                            prefix={<SearchOutlined />}
                            onChange={handleSearch}
                            style={{ width: 350 }}
                            allowClear
                        />
                    </Space>
                    <Typography.Text>
                        Всего: {total}
                    </Typography.Text>
                </div>

                {initialLoading ? (
                    <div style={{ textAlign: 'center', padding: '50px 0' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <>
                        <style>
                            {`
                                .table-horizontal-borders .ant-table-tbody > tr > td {
                                    border-left: none !important;
                                    border-right: none !important;
                                }
                                .table-horizontal-borders .ant-table-thead > tr > th {
                                    border-left: none !important;
                                    border-right: none !important;
                                }
                                .table-horizontal-borders .ant-table-tbody > tr:hover > td {
                                    background-color: #f5f5f5 !important;
                                }
                            `}
                        </style>
                        <Table
                            columns={columns}
                            dataSource={tableData}
                            pagination={false}
                            size="middle"
                            bordered={false}
                            className="table-horizontal-borders"
                            tableLayout="fixed"
                            scroll={{x: 1000}}
                            onRow={(record) => ({
                                onClick: () => navigate(`/payment-methods/${record.id}`),
                                style: {cursor: 'pointer'}
                            })}
                        />
                        {loading && (
                            <div style={{textAlign: 'center', padding: '20px 0'}}>
                                <Spin/>
                            </div>
                        )}
                        {/* Элемент для отслеживания прокрутки */}
                        {hasMore && (
                            <div
                                ref={lastPaymentMethodElementRef}
                                style={{height: 10, width: '100%'}}
                            />
                        )}
                        {!hasMore && paymentMethods.length > 0 && (
                            <div style={{textAlign: 'center', padding: '10px'}}>
                                <Typography.Text type="secondary">
                                    Больше записей нет
                                </Typography.Text>
                            </div>
                        )}
                    </>
                )}
            </Card>
        </>
    );
};

export default PaymentMethodsList;